/**
 * src/utils/debtMinimizer.ts
 * Optimal Multi-Party Debt Minimization Algorithm
 * Reduces N-way group debts to the minimum possible number of direct cash settlements
 */

import { type UserItem } from '../services/api';

export interface SettlementTransfer {
  id: string;
  fromUser: UserItem;
  toUser: UserItem;
  amount: number;
}

export function minimizeDebts(users: UserItem[]): SettlementTransfer[] {
  // 1. Separate into debtors (< 0) and creditors (> 0)
  // In CKAudit convention: current_balance < 0 means user has paid less than received (owes money).
  // current_balance > 0 means user has paid more (credit due to user).
  interface BalanceNode {
    user: UserItem;
    amount: number;
  }

  const debtors: BalanceNode[] = [];
  const creditors: BalanceNode[] = [];

  for (const user of users) {
    const bal = Math.round((user.current_balance || 0) * 100) / 100;
    if (bal < -0.01) {
      debtors.push({ user, amount: Math.abs(bal) });
    } else if (bal > 0.01) {
      creditors.push({ user, amount: bal });
    }
  }

  // Sort descending by amount for greedy resolution
  debtors.sort((a, b) => b.amount - a.amount);
  creditors.sort((a, b) => b.amount - a.amount);

  const transfers: SettlementTransfer[] = [];
  let dIdx = 0;
  let cIdx = 0;

  while (dIdx < debtors.length && cIdx < creditors.length) {
    const debtor = debtors[dIdx];
    const creditor = creditors[cIdx];

    const amount = Math.min(debtor.amount, creditor.amount);
    const roundedAmount = Math.round(amount * 100) / 100;

    if (roundedAmount > 0) {
      transfers.push({
        id: `${debtor.user.id}->${creditor.user.id}-${transfers.length}`,
        fromUser: debtor.user,
        toUser: creditor.user,
        amount: roundedAmount
      });
    }

    debtor.amount -= amount;
    creditor.amount -= amount;

    if (debtor.amount < 0.01) dIdx++;
    if (creditor.amount < 0.01) cIdx++;
  }

  return transfers;
}
